// Shapes with curves (Year 8, 2 Nov 2026). A `curved_shape` is half a
// circle, a quarter of a circle, a rectangle with half a circle on its right
// side, or a running track (two straight sides joined by half circles at
// each end). Its sides carry labels by where they are ("bottom", "left",
// "curve" and so on): a length ("12 cm"), a letter, "?" for the side to find,
// or nothing. Where a half circle meets the straight sides, a dashed line
// is drawn across the join. It may also say "not drawn to scale".
//
// The words say what is drawn and what is written on it, nothing else
// (wording approved by the owner, 2 Nov 2026). A length is never worked
// out, and the shape is never named beyond the approved outline sentence.
//
// A rectangle with a half circle and a track are drawn in proportion to
// their labels, within limits that keep them readable. Half and quarter
// circles are always the same drawing.
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

export const CURVED_SHAPES = [
  'semicircle',
  'quarter_circle',
  'rectangle_semicircle',
  'track',
] as const;
export type CurvedShapeName = (typeof CURVED_SHAPES)[number];

export type CurvedPlace =
  | 'straight'
  | 'bottom'
  | 'top'
  | 'left'
  | 'curve'
  | 'join'
  | 'left_end'
  | 'right_end';

// The places each shape can carry a label, in the order they are read.
//  - semicircle: its straight side at the bottom, and its curved side.
//  - quarter_circle: the right angle at the bottom left; its straight side
//    along the bottom and up the left, and its curved side.
//  - rectangle_semicircle: the rectangle's bottom, top and left sides, the
//    half circle's curved side, and the dashed line where they join.
//  - track: the straight sides along the top and the bottom, and the dashed
//    lines across the left and the right ends.
export const CURVED_PLACES: Record<CurvedShapeName, CurvedPlace[]> = {
  semicircle: ['straight', 'curve'],
  quarter_circle: ['bottom', 'left', 'curve'],
  rectangle_semicircle: ['bottom', 'left', 'top', 'curve', 'join'],
  track: ['top', 'bottom', 'left_end', 'right_end'],
};

export type CurvedLabel = { place: CurvedPlace; text: string };

export type CurvedShapeSpec = {
  type: 'curved_shape';
  shape: CurvedShapeName;
  // Only the places that carry a label, in the order of CURVED_PLACES.
  labels: CurvedLabel[];
  // Shows "not drawn to scale" under the picture, and says it.
  notToScale: boolean;
};

const LENGTH = /^\d+(?:\.\d+)?(?: ?(?:mm|cm|m|km))?$/;
const LETTER = /^[a-zA-Z]$/;
// A longer label would not fit beside a short side on a phone.
const LONGEST_LABEL = 10;

function isLabel(text: string): boolean {
  return (
    text.length <= LONGEST_LABEL &&
    (text === '?' || LETTER.test(text) || LENGTH.test(text))
  );
}

export function readCurvedShape(
  record: Record<string, unknown>,
): CurvedShapeSpec | null {
  const shape = CURVED_SHAPES.find((name) => name === record.shape);
  if (!shape) return null;
  const notToScale = record.notToScale ?? false;
  if (typeof notToScale !== 'boolean') return null;
  const given = record.labels ?? {};
  if (typeof given !== 'object' || Array.isArray(given)) return null;
  const entries = given as Record<string, unknown>;
  const places = CURVED_PLACES[shape];
  // A label in a place the shape does not have is refused.
  if (
    Object.keys(entries).some((key) => !places.some((place) => place === key))
  )
    return null;
  const labels: CurvedLabel[] = [];
  for (const place of places) {
    const value = entries[place];
    if (value === undefined) continue;
    if (typeof value !== 'string') return null;
    const text = value.trim();
    if (text === '') continue;
    if (!isLabel(text)) return null;
    labels.push({ place, text });
  }
  return { type: 'curved_shape', shape, labels, notToScale };
}

const OUTLINE_WORDS: Record<CurvedShapeName, string> = {
  semicircle: 'Half a circle, with its straight side at the bottom.',
  quarter_circle: 'A quarter of a circle.',
  rectangle_semicircle: 'A rectangle with half a circle on its right side.',
  track: 'A shape with two straight sides joined by half circles at each end.',
};

// Where a label is, in a list of labels.
const PLACE_WORDS: Record<CurvedPlace, string> = {
  straight: 'on the straight side',
  bottom: 'along the bottom',
  top: 'along the top',
  left: 'on the left',
  curve: 'on the curved side',
  join: 'on the dashed line where the half circle joins',
  left_end: 'on the dashed line across the left end',
  right_end: 'on the dashed line across the right end',
};

// The side a label is on, as the subject of a sentence.
const SIDE_WORDS: Partial<Record<CurvedPlace, string>> = {
  straight: 'The straight side',
  bottom: 'The straight side along the bottom',
  left: 'The straight side on the left',
  curve: 'The curved side',
};

function spokenLabel(text: string): string {
  if (text === '?') return 'a question mark';
  return LETTER.test(text) ? `the letter ${text}` : text;
}

function sideSentence(side: string, text: string): string {
  return text === '?'
    ? `${side} is unknown and is marked with a question mark.`
    : `${side} is labelled ${spokenLabel(text)}.`;
}

// What a screen reader says: the outline, then the labels, then "Not drawn
// to scale." when the picture says so.
export function describeCurvedShape(spec: CurvedShapeSpec): string {
  const parts = [OUTLINE_WORDS[spec.shape]];
  const { labels } = spec;
  if (spec.shape === 'semicircle' || spec.shape === 'quarter_circle') {
    const bottom = labels.find((label) => label.place === 'bottom');
    const left = labels.find((label) => label.place === 'left');
    const both = bottom !== undefined && bottom.text === left?.text;
    for (const label of labels) {
      if (both && label.place === 'left') continue;
      if (both && label.place === 'bottom') {
        parts.push(
          label.text === '?'
            ? 'Both straight sides are unknown and are marked with a question mark.'
            : `Both straight sides are labelled ${spokenLabel(label.text)}.`,
        );
        continue;
      }
      parts.push(sideSentence(SIDE_WORDS[label.place] ?? '', label.text));
    }
  } else if (labels.length > 0) {
    const listed: string[] = [];
    const top = labels.find((label) => label.place === 'top');
    const bottom = labels.find((label) => label.place === 'bottom');
    // A track with the same label on both straight sides says it once.
    const pair =
      spec.shape === 'track' && top !== undefined && top.text === bottom?.text;
    for (const label of labels) {
      if (pair && label.place === 'bottom') continue;
      const where =
        pair && label.place === 'top'
          ? 'along the top and the bottom'
          : PLACE_WORDS[label.place];
      listed.push(`${spokenLabel(label.text)} ${where}`);
    }
    parts.push(`Labels: ${listed.join(', ')}.`);
  }
  if (spec.notToScale) parts.push('Not drawn to scale.');
  return parts.join(' ');
}

// ---------------------------------------------------------------------------
// Where everything is drawn, in units close to one pixel each at the
// picture's natural width. y goes down the page.

export type CurvedPoint = { x: number; y: number };
// A label's box, centred on (x, y).
export type CurvedText = CurvedPoint & {
  text: string;
  place: CurvedPlace | 'scale';
  width: number;
  height: number;
};

export type CurvedPicture = {
  // The outline, as the `d` of an SVG path.
  outline: string;
  // Dashed lines across the joins of a half circle and straight sides.
  dashes: { from: CurvedPoint; to: CurvedPoint }[];
  texts: CurvedText[];
  box: { x: number; y: number; width: number; height: number };
};

export const CURVED_LABEL_SIZE = 14;
export const CURVED_NOTE_SIZE = 12;
export const NOT_TO_SCALE = 'not drawn to scale';
const LABEL_HEIGHT = 15;
const NOTE_HEIGHT = 13;
const GAP = 8;
const PAD = 6;
const SEMICIRCLE_RADIUS = 72;
const QUARTER_RADIUS = 110;

const fixed = (value: number) => value.toFixed(2);
const clamp = (value: number, low: number, high: number) =>
  Math.min(high, Math.max(low, value));

// Roughly how wide a label is drawn, to keep it off the lines.
function labelWidth(text: string): number {
  return text.length * 8.6;
}

// The number in a length label, or null.
function lengthOf(text: string | undefined): number | null {
  if (text === undefined || !LENGTH.test(text)) return null;
  const size = Number(/^\d+(?:\.\d+)?/.exec(text)?.[0]);
  return size > 0 ? size : null;
}

// The unit of a length label ("" when it has none).
function unitOf(text: string | undefined): string | null {
  if (text === undefined || !LENGTH.test(text)) return null;
  return text.replace(/^\d+(?:\.\d+)?\s?/, '');
}

// Two lengths compared, when they share a unit; null otherwise.
function ratioOf(
  long: string | undefined,
  short: string | undefined,
): number | null {
  const a = lengthOf(long);
  const b = lengthOf(short);
  if (a === null || b === null || unitOf(long) !== unitOf(short)) return null;
  return a / b;
}

export function curvedShapePicture(spec: CurvedShapeSpec): CurvedPicture {
  const text = (place: CurvedPlace) =>
    spec.labels.find((label) => label.place === place)?.text;
  const texts: CurvedText[] = [];
  const put = (place: CurvedPlace, x: number, y: number) => {
    const label = text(place);
    if (label === undefined) return;
    texts.push({
      x,
      y,
      text: label,
      place,
      width: labelWidth(label),
      height: LABEL_HEIGHT,
    });
  };
  // Half a label's width or height, for placing it beside a line.
  const halfWidth = (place: CurvedPlace) => labelWidth(text(place) ?? '') / 2;
  const below = GAP + LABEL_HEIGHT / 2;
  const dashes: CurvedPicture['dashes'] = [];
  // The corners of the drawing, before any label.
  let reach: CurvedPoint[];
  let outline: string;

  switch (spec.shape) {
    case 'semicircle': {
      const r = SEMICIRCLE_RADIUS;
      outline = `M ${fixed(-r)} 0 A ${fixed(r)} ${fixed(r)} 0 0 1 ${fixed(r)} 0 Z`;
      reach = [
        { x: -r, y: -r },
        { x: r, y: 0 },
      ];
      put('straight', 0, below);
      put('curve', 0, -r - below);
      break;
    }
    case 'quarter_circle': {
      // The right angle is at the bottom left, at 0, 0.
      const r = QUARTER_RADIUS;
      outline = `M 0 0 L ${fixed(r)} 0 A ${fixed(r)} ${fixed(r)} 0 0 0 0 ${fixed(-r)} Z`;
      reach = [
        { x: 0, y: -r },
        { x: r, y: 0 },
      ];
      put('bottom', r / 2, below);
      put('left', -GAP - halfWidth('left'), -r / 2);
      // Outside the middle of the curve.
      const way = Math.SQRT1_2;
      const out = GAP + Math.hypot(halfWidth('curve'), LABEL_HEIGHT / 2);
      put('curve', way * (r + out), -way * (r + out));
      break;
    }
    case 'rectangle_semicircle': {
      // The rectangle runs from 0 to `long` across and from 0 up to
      // `across`; the half circle on its right side bulges out by
      // `across` / 2. Drawn in proportion to the labels, within limits.
      const ratio =
        ratioOf(text('bottom') ?? text('top'), text('left') ?? text('join')) ??
        1.6;
      const shape = clamp(ratio, 0.6, 3);
      // At most 200 wide (half circle included) and 110 tall.
      const across = Math.min(110, 200 / (shape + 0.5));
      const long = across * shape;
      const r = across / 2;
      outline = `M 0 0 L ${fixed(long)} 0 A ${fixed(r)} ${fixed(r)} 0 0 0 ${fixed(long)} ${fixed(-across)} L 0 ${fixed(-across)} Z`;
      dashes.push({ from: { x: long, y: 0 }, to: { x: long, y: -across } });
      reach = [
        { x: 0, y: -across },
        { x: long + r, y: 0 },
      ];
      put('bottom', long / 2, below);
      put('top', long / 2, -across - below);
      put('left', -GAP - halfWidth('left'), -r);
      put('curve', long + r + GAP + halfWidth('curve'), -r);
      // Inside the rectangle, just left of the dashed line.
      put('join', long - GAP - halfWidth('join'), -r);
      break;
    }
    case 'track': {
      // The straight sides run from 0 to `long`; each end is a half circle
      // `across` high. Drawn in proportion to the labels, within limits.
      const ratio =
        ratioOf(
          text('top') ?? text('bottom'),
          text('left_end') ?? text('right_end'),
        ) ?? 1.6;
      const shape = clamp(ratio, 0.8, 3);
      // At most 230 wide (both ends included) and 110 tall.
      const across = Math.min(110, 230 / (shape + 1));
      const long = across * shape;
      const r = across / 2;
      outline = `M 0 0 L ${fixed(long)} 0 A ${fixed(r)} ${fixed(r)} 0 0 0 ${fixed(long)} ${fixed(-across)} L 0 ${fixed(-across)} A ${fixed(r)} ${fixed(r)} 0 0 0 0 0 Z`;
      dashes.push(
        { from: { x: 0, y: 0 }, to: { x: 0, y: -across } },
        { from: { x: long, y: 0 }, to: { x: long, y: -across } },
      );
      reach = [
        { x: -r, y: -across },
        { x: long + r, y: 0 },
      ];
      put('top', long / 2, -across - below);
      put('bottom', long / 2, below);
      // Inside the straight part, beside each dashed line.
      put('left_end', GAP + halfWidth('left_end'), -r);
      put('right_end', long - GAP - halfWidth('right_end'), -r);
      break;
    }
  }

  const xs = reach.map((point) => point.x);
  const ys = reach.map((point) => point.y);
  for (const label of texts) {
    xs.push(label.x - label.width / 2, label.x + label.width / 2);
    ys.push(label.y - label.height / 2, label.y + label.height / 2);
  }
  if (spec.notToScale) {
    // Under everything, at the left.
    const width = NOT_TO_SCALE.length * 6.6;
    const left = Math.min(...xs);
    texts.push({
      x: left + width / 2,
      y: Math.max(...ys) + GAP + NOTE_HEIGHT / 2,
      text: NOT_TO_SCALE,
      place: 'scale',
      width,
      height: NOTE_HEIGHT,
    });
    xs.push(left + width);
    ys.push(Math.max(...ys) + GAP + NOTE_HEIGHT);
  }
  const x = Math.min(...xs) - PAD;
  const y = Math.min(...ys) - PAD;
  return {
    outline,
    dashes,
    texts,
    box: {
      x,
      y,
      width: Math.max(...xs) + PAD - x,
      height: Math.max(...ys) + PAD - y,
    },
  };
}
